import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Background sync settings (Section 12 step 5). The scheduler reads the
 * frequency; Settings writes it. Persisted so the choice survives reloads.
 */
export type SyncFrequency = "off" | "12h" | "24h" | "36h";

export const SYNC_FREQUENCY_MS: Record<SyncFrequency, number | null> = {
  off: null,
  "12h": 12 * 60 * 60 * 1000,
  "24h": 24 * 60 * 60 * 1000,
  "36h": 36 * 60 * 60 * 1000,
};

export const SYNC_FREQUENCY_LABELS: Record<SyncFrequency, string> = {
  off: "Off",
  "12h": "Every 12 hours",
  "24h": "Every 24 hours",
  "36h": "Every 36 hours",
};

/** Default when nothing is stored. Once a day: a provider adds titles daily,
 * and on a TV each refresh is a download plus a rebuild (owner, 2026-09-20). */
export const DEFAULT_SYNC_FREQUENCY: SyncFrequency = "24h";

/**
 * Frequencies offered before the 2026-09-20 change to off/12/24/36 (and the
 * 2026-08-27 change before it). This
 * store is persisted, so a browser that chose one of these still has it in
 * localStorage. Left unmapped, SYNC_FREQUENCY_MS[stale] is `undefined`, which
 * slips past the scheduler's `=== null` check and reaches
 * `setInterval(run, undefined)` -- a 0ms interval that refetches the catalog
 * and runs a health cycle in a tight loop, against a provider capped at five
 * connections. Each legacy value maps to the nearest surviving option.
 */
const LEGACY_FREQUENCIES: Record<string, SyncFrequency> = {
  "30m": "12h",
  "1h": "12h",
  "2h": "12h",
  "4h": "12h",
  "6h": "12h",
  "8h": "12h",
  daily: "24h",
};

/** Coerce any stored value to a valid frequency, mapping legacy choices. */
export function normalizeSyncFrequency(value: unknown): SyncFrequency {
  if (typeof value !== "string") return DEFAULT_SYNC_FREQUENCY;
  if (value in SYNC_FREQUENCY_MS) return value as SyncFrequency;
  return LEGACY_FREQUENCIES[value] ?? DEFAULT_SYNC_FREQUENCY;
}

interface SyncSettingsStore {
  frequency: SyncFrequency;
  /** ISO timestamp of the last successful background refresh. */
  lastUpdatedAt: string | null;
  /**
   * Hide a channel that failed three separate tries, keep it, and test it again at every update
   * (owner, 2026-10-21: on by default). Off: nothing is hidden, and what was hidden comes back.
   */
  hideFailingChannels: boolean;
  setFrequency: (frequency: SyncFrequency) => void;
  markUpdated: () => void;
  setHideFailingChannels: (on: boolean) => void;
}

export const useSyncSettings = create<SyncSettingsStore>()(
  persist(
    (set) => ({
      frequency: DEFAULT_SYNC_FREQUENCY,
      lastUpdatedAt: null,
      hideFailingChannels: true,
      setFrequency: (frequency) => set({ frequency }),
      markUpdated: () => set({ lastUpdatedAt: new Date().toISOString() }),
      setHideFailingChannels: (hideFailingChannels) => set({ hideFailingChannels }),
    }),
    {
      name: "slique-sync-settings",
      // v2: the frequency options changed to off/4h/8h/12h/24h. Rehydrating a
      // v1 choice ("2h", "daily", ...) without mapping it would leave an
      // unusable value in the select and a 0ms sync interval -- see
      // LEGACY_FREQUENCIES.
      version: 2,
      migrate: (persisted) => {
        const state = (persisted ?? {}) as Partial<SyncSettingsStore>;
        return { ...state, frequency: normalizeSyncFrequency(state.frequency) } as SyncSettingsStore;
      },
      // A value can also arrive stale without a version bump (hand-edited
      // storage, or a profile synced from an older build), so normalize on
      // every rehydrate too.
      onRehydrateStorage: () => (state) => {
        if (state) state.frequency = normalizeSyncFrequency(state.frequency);
      },
    },
  ),
);

const CATALOG_TTL_FALLBACK_MS = 24 * 60 * 60 * 1000;

/**
 * How old the device's catalog may get before it refreshes by itself — the
 * viewer's sync frequency. "Off" means off: nothing refreshes by itself, only
 * Settings' "Refresh now". Shared by the device API (when to download again)
 * and useDeviceSeededQuery (when a launch rebuilds the lists at all).
 */
export function catalogRefreshAfterMs(): number {
  const frequency = useSyncSettings.getState().frequency;
  if (frequency === "off") return Number.MAX_SAFE_INTEGER;
  return SYNC_FREQUENCY_MS[frequency] ?? CATALOG_TTL_FALLBACK_MS;
}
